/* eslint-disable @typescript-eslint/no-explicit-any */
import { FormationProvider } from "@fanduel/formation-react-components";
import { THEMES } from "@fanduel/formation-theming";
import { LinkProvider } from "@fanduel/og-fe-library";
import {
  HydrationBoundary,
  QueryCache,
  QueryClient,
  QueryClientProvider,
} from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import App, { AppContext } from "next/app";
import getConfig from "next/config";
import Link from "next/link";
import Script from "next/script";
import { DefaultSeo, OrganizationJsonLd } from "next-seo";
import React from "react";
import ErrorBoundary from "@/components/empty/ErrorBoundary";
import DefaultLayout from "@/components/layout/Layout";
import Rum from "@/components/Rum";
import { SEO } from "@/constants/index";
import { PUBLISHER_LOGO } from "@/constants/seo";
import { AmplitudeProvider } from "@/utils/hooks/useAmplitude";
import isClient from "@/utils/isClient";
import logger from "@/utils/logger";
import type { NextPage } from "next";
import type { AppProps } from "next/app";
import type { ReactElement, ReactNode } from "react";
import "../styles/global.scss";
import "../public/fonts/fonts.css";

export type NextPageWithLayout<P = Record<string, unknown>> = NextPage<P> & {
  getLayout?: (page: ReactElement) => ReactNode;
};

export type AppPropsWithLayout = AppProps & {
  Component: NextPageWithLayout;
};

const { publicRuntimeConfig } = getConfig();

if (publicRuntimeConfig.API_MOCKING === "enabled") {
  logger.log(
    `Startup: Initializing MSW ${isClient() ? "browser" : "server"}-side`
  );
  const { initServerMocks, initBrowserMocks } = await import(
    "../mocks/mswInit"
  );
  if (isClient()) {
    await initBrowserMocks();
  } else {
    await initServerMocks();
  }
}

export default function MyApp({ Component, pageProps }: AppPropsWithLayout) {
  const [queryClient] = React.useState(
    () =>
      new QueryClient({
        queryCache: new QueryCache({
          onError: (error, query) => {
            if (query.meta?.errorMessage)
              logger.error(query.meta, query.meta.errorMessage);
          },
        }),
        defaultOptions: {
          queries: {
            staleTime: 100000,
          },
        },
      })
  );

  const applyLayout =
    Component.getLayout ??
    ((page: ReactElement) => (
      <DefaultLayout {...pageProps}>{page}</DefaultLayout>
    ));

  return (
    <>
      {publicRuntimeConfig.SEARCH_ENGINES ? (
        <Script
          data-testid="google-tag-manager"
          id="google-tag-manager"
          strategy="afterInteractive"
        >
          {`(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
  new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
  j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
  'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
  })(window,document,'script','dataLayer','GTM-N5T8BZG');`}
        </Script>
      ) : (
        <Script
          data-testid="google-tag-manager-dev"
          id="google-tag-manager"
          strategy="afterInteractive"
        >
          {`(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
'https://www.googletagmanager.com/gtm.js?id='+i+dl+ '&gtm_auth=oyyylYgsNvrsgOVavBH3Zw&gtm_preview=env-3&gtm_cookies_win=x';f.parentNode.insertBefore(j,f);
})(window,document,'script','dataLayer','GTM-N5T8BZG');`}
        </Script>
      )}
      <QueryClientProvider client={queryClient}>
        <HydrationBoundary state={pageProps.dehydratedState}>
          <FormationProvider theme={THEMES.fanduel}>
            <Rum />
            <AmplitudeProvider>
              <LinkProvider CustomLink={Link}>
                <DefaultSeo
                  titleTemplate={`%s | ${SEO.SITE_NAME}`}
                  dangerouslySetAllPagesToNoIndex={
                    !publicRuntimeConfig.SEARCH_ENGINES
                  }
                  dangerouslySetAllPagesToNoFollow={
                    !publicRuntimeConfig.SEARCH_ENGINES
                  }
                  openGraph={{
                    type: "website",
                    locale: "en_US",
                    url: SEO.FANDUEL_URL_RESEARCH,
                    siteName: SEO.SITE_NAME,
                  }}
                  twitter={{
                    handle: SEO.TWITTERHANDLE,
                    site: SEO.TWITTERHANDLE,
                    cardType: "summary_large_image",
                  }}
                />
                <OrganizationJsonLd
                  id={SEO.ORGANIZATION_ID}
                  name={SEO.SITE_NAME}
                  url={SEO.FANDUEL_URL}
                  logo={PUBLISHER_LOGO}
                  sameAs={SEO.SAME_AS}
                />
                <ErrorBoundary>
                  {applyLayout(<Component {...pageProps} />)}
                </ErrorBoundary>
              </LinkProvider>
            </AmplitudeProvider>
          </FormationProvider>
          <ReactQueryDevtools initialIsOpen={false} />
        </HydrationBoundary>
      </QueryClientProvider>
    </>
  );
}
MyApp.getInitialProps = async function (context: AppContext) {
  const ctx = await App.getInitialProps(context);
  return { ...ctx };
};
